<template>
    <div :class="['otoast', `otoast--${type}`]" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="otoast__icon">
            <i :class="iconClass"></i>
        </div>
        <div class="otoast__content">
            <h4 class="otoast__title">{{ title }}</h4>
            <p class="otoast__message">{{ message }}</p>
        </div>
        <button class="otoast__close" @click="close" aria-label="Close">
            &times;
        </button>
    </div>
</template>

<script>
import { onMounted, onUnmounted, computed } from 'vue'

/**
 * OToast is a single notification component.
 * It displays a message with a title, an icon corresponding to the type, and auto-closes after a duration.
 */
export default {
    name: 'OToast',
    props: {
        /**
         * The type of the toast, which determines the icon and color.
         * @values 'success', 'error', 'warning', 'info'
         */
        type: {
            type: String,
            default: 'info',
            validator: (value) => ['success', 'error', 'warning', 'info'].includes(value),
        },
        /**
         * The title of the toast notification.
         */
        title: {
            type: String,
            required: true,
        },
        /**
         * The main content/message of the toast.
         */
        message: {
            type: String,
            required: true,
        },
        /**
         * The duration in milliseconds before the toast automatically closes.
         * Set to 0 to disable auto-close.
         */
        duration: {
            type: Number,
            default: 5000,
        },
    },
    /**
     * Emitted when the toast is closed, either by user action or timer.
     */
    emits: ['close'],
    setup(props, { emit }) {
        let timer = null

        const iconClass = computed(() => {
            switch (props.type) {
                case 'success':
                    return 'fas fa-check-circle'
                case 'error':
                    return 'fas fa-times-circle'
                case 'warning':
                    return 'fas fa-exclamation-triangle'
                default:
                    return 'fas fa-info-circle'
            }
        })

        function close() {
            emit('close')
        }

        onMounted(() => {
            if (props.duration > 0) {
                timer = setTimeout(close, props.duration)
            }
        })

        onUnmounted(() => {
            if (timer) {
                clearTimeout(timer)
            }
        })

        return {
            iconClass,
            close,
        }
    },
}
</script>

<style scoped>
.otoast {
    display: flex;
    align-items: flex-start;
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: #fff;
    border: 1px solid transparent;
    max-width: 550px;
    pointer-events: auto;
}

.otoast--success {
    border-left: 5px solid #4caf50;
}

.otoast--error {
    border-left: 5px solid #f44336;
}

.otoast--warning {
    border-left: 5px solid #ff9800;
}

.otoast--info {
    border-left: 5px solid #2196f3;
}

.otoast__icon {
    flex-shrink: 0;
    margin-right: 1rem;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    height: 100%;
}

.otoast--success .otoast__icon {
    color: #4caf50;
}

.otoast--error .otoast__icon {
    color: #f44336;
}

.otoast--warning .otoast__icon {
    color: #ff9800;
}

.otoast--info .otoast__icon {
    color: #2196f3;
}

.otoast__content {
    flex-grow: 1;
}

.otoast__title {
    margin: 0;
    font-weight: 600;
    font-size: 1rem;
    color: #333;
}

.otoast__message {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
    color: #666;
}

.otoast__close {
    flex-shrink: 0;
    margin-left: 1rem;
    background: transparent;
    border: none;
    color: #999;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    opacity: 0.7;
    padding: 0;
}

.otoast__close:hover {
    opacity: 1;
}
</style>